import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Space,
    Button,
    Alert,
    Row,
    Col,
    Spin,
    Input,
    message,
    Form,
    Divider,
} from 'antd';
import {
    DollarOutlined,
    ReloadOutlined,
    SaveOutlined,
    CreditCardOutlined,
    GiftOutlined,
} from '@ant-design/icons';
import {
    useGetSystemSettingsByCategoryQuery,
    useUpdateSystemSettingMutation,
    useCreateSystemSettingMutation,
} from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';

const { Text, Title } = Typography;

interface BillingSettings {
    // Stripe Price IDs
    STRIPE_PROFESSIONAL_MONTHLY_PRICE_ID: string;
    STRIPE_PROFESSIONAL_YEARLY_PRICE_ID: string;
    STRIPE_ENTERPRISE_MONTHLY_PRICE_ID: string;
    STRIPE_ENTERPRISE_YEARLY_PRICE_ID: string;
    // Display Prices (USD)
    STRIPE_PROFESSIONAL_MONTHLY_PRICE_USD: string;
    STRIPE_PROFESSIONAL_YEARLY_PRICE_USD: string;
    STRIPE_ENTERPRISE_MONTHLY_PRICE_USD: string;
    STRIPE_ENTERPRISE_YEARLY_PRICE_USD: string;
    // Promotions
    ANNUAL_DISCOUNT_COUPON: string;
}

const DEFAULT_SETTINGS: BillingSettings = {
    STRIPE_PROFESSIONAL_MONTHLY_PRICE_ID: '',
    STRIPE_PROFESSIONAL_YEARLY_PRICE_ID: '',
    STRIPE_ENTERPRISE_MONTHLY_PRICE_ID: '',
    STRIPE_ENTERPRISE_YEARLY_PRICE_ID: '',
    STRIPE_PROFESSIONAL_MONTHLY_PRICE_USD: '990.00',
    STRIPE_PROFESSIONAL_YEARLY_PRICE_USD: '9900.00',
    STRIPE_ENTERPRISE_MONTHLY_PRICE_USD: '1500.00',
    STRIPE_ENTERPRISE_YEARLY_PRICE_USD: '15000.00',
    ANNUAL_DISCOUNT_COUPON: '',
};

export const BillingSettingsTab: React.FC = () => {
    const { user } = useAuth();
    const jwtToken = user?.jwtToken || '';
    const [form] = Form.useForm();
    const [hasChanges, setHasChanges] = useState(false);

    // RTK Query hooks
    const { data: categorizedData, isLoading: loading, refetch } = useGetSystemSettingsByCategoryQuery({ jwtToken });
    const [updateSetting, { isLoading: updating }] = useUpdateSystemSettingMutation();
    const [createSetting, { isLoading: creating }] = useCreateSystemSettingMutation();

    // Flatten all settings from categories
    const allSettings = React.useMemo(() => {
        if (!categorizedData?.categories) return [];
        return categorizedData.categories.flatMap(category => category.settings);
    }, [categorizedData]);

    // Get current values from settings
    const getCurrentSettings = (): BillingSettings => {
        const settings = { ...DEFAULT_SETTINGS };
        Object.keys(DEFAULT_SETTINGS).forEach(key => {
            const setting = allSettings.find((s: any) => s.name === key);
            if (setting) {
                settings[key as keyof BillingSettings] = setting.value;
            }
        });
        return settings;
    };

    // Initialize form with current values
    useEffect(() => {
        if (allSettings.length > 0) {
            form.setFieldsValue(getCurrentSettings());
        }
    }, [allSettings, form]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Update each setting
            for (const [key, value] of Object.entries(values)) {
                const existingSetting = allSettings.find((s: any) => s.name === key);
                if (existingSetting) {
                    if (existingSetting.value !== value) {
                        await updateSetting({
                            jwtToken,
                            settingName: key,
                            payload: { value: value as string },
                        }).unwrap();
                    }
                } else if (value) {
                    await createSetting({
                        jwtToken,
                        payload: {
                            name: key,
                            value: value as string,
                            type: 'STRING',
                        },
                    }).unwrap();
                }
            }

            message.success('Billing settings saved successfully');
            setHasChanges(false);
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to save settings');
        }
    };

    const handleFormChange = () => {
        setHasChanges(true);
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            <Alert
                message="Billing & Stripe Configuration"
                description="Configure Stripe price IDs and display prices for subscription plans. Price IDs are obtained from your Stripe Dashboard under Products."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Text type="secondary">
                        Changes will take effect immediately for new subscriptions
                    </Text>
                </div>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => refetch()}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={updating || creating}
                        disabled={!hasChanges}
                    >
                        Save Changes
                    </Button>
                </Space>
            </div>

            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
            >
                <Row gutter={24}>
                    {/* Professional Plan */}
                    <Col xs={24} lg={12}>
                        <Card
                            title={
                                <Space>
                                    <CreditCardOutlined />
                                    <span>Professional Plan</span>
                                </Space>
                            }
                            style={{ marginBottom: 24 }}
                        >
                            <Title level={5} style={{ marginBottom: 16 }}>
                                <DollarOutlined style={{ marginRight: 8 }} />
                                Display Prices (USD)
                            </Title>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="STRIPE_PROFESSIONAL_MONTHLY_PRICE_USD"
                                        label="Monthly Price"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input prefix="$" suffix="USD" placeholder="990.00" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="STRIPE_PROFESSIONAL_YEARLY_PRICE_USD"
                                        label="Yearly Price"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input prefix="$" suffix="USD" placeholder="9900.00" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title level={5} style={{ marginBottom: 16 }}>
                                <CreditCardOutlined style={{ marginRight: 8 }} />
                                Stripe Price IDs
                            </Title>
                            <Form.Item
                                name="STRIPE_PROFESSIONAL_MONTHLY_PRICE_ID"
                                label="Monthly Price ID"
                                extra="From Stripe Dashboard: Products > Professional > Monthly price"
                            >
                                <Input placeholder="price_1ABC123..." style={{ fontFamily: 'monospace' }} />
                            </Form.Item>
                            <Form.Item
                                name="STRIPE_PROFESSIONAL_YEARLY_PRICE_ID"
                                label="Yearly Price ID"
                                extra="From Stripe Dashboard: Products > Professional > Yearly price"
                            >
                                <Input placeholder="price_1ABC123..." style={{ fontFamily: 'monospace' }} />
                            </Form.Item>
                        </Card>
                    </Col>

                    {/* Enterprise Plan */}
                    <Col xs={24} lg={12}>
                        <Card
                            title={
                                <Space>
                                    <CreditCardOutlined />
                                    <span>Enterprise Plan</span>
                                </Space>
                            }
                            style={{ marginBottom: 24 }}
                        >
                            <Title level={5} style={{ marginBottom: 16 }}>
                                <DollarOutlined style={{ marginRight: 8 }} />
                                Display Prices (USD)
                            </Title>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="STRIPE_ENTERPRISE_MONTHLY_PRICE_USD"
                                        label="Monthly Price"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input prefix="$" suffix="USD" placeholder="1500.00" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="STRIPE_ENTERPRISE_YEARLY_PRICE_USD"
                                        label="Yearly Price"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input prefix="$" suffix="USD" placeholder="15000.00" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider />

                            <Title level={5} style={{ marginBottom: 16 }}>
                                <CreditCardOutlined style={{ marginRight: 8 }} />
                                Stripe Price IDs
                            </Title>
                            <Form.Item
                                name="STRIPE_ENTERPRISE_MONTHLY_PRICE_ID"
                                label="Monthly Price ID"
                                extra="From Stripe Dashboard: Products > Enterprise > Monthly price"
                            >
                                <Input placeholder="price_1ABC123..." style={{ fontFamily: 'monospace' }} />
                            </Form.Item>
                            <Form.Item
                                name="STRIPE_ENTERPRISE_YEARLY_PRICE_ID"
                                label="Yearly Price ID"
                                extra="From Stripe Dashboard: Products > Enterprise > Yearly price"
                            >
                                <Input placeholder="price_1ABC123..." style={{ fontFamily: 'monospace' }} />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>

                {/* Promotions */}
                <Card
                    title={
                        <Space>
                            <GiftOutlined />
                            <span>Promotions & Discounts</span>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Form.Item
                        name="ANNUAL_DISCOUNT_COUPON"
                        label="Annual First-Year Discount Coupon"
                        extra="Stripe coupon code for first-year annual discount (e.g., 40% off). Leave empty to disable the promotion."
                    >
                        <Input placeholder="ANNUAL40" style={{ fontFamily: 'monospace', maxWidth: 300 }} />
                    </Form.Item>
                    <Text type="secondary">
                        The discount percentage is determined by the coupon configuration in Stripe Dashboard.
                    </Text>
                </Card>
            </Form>

            <Alert
                message="Important"
                description={
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                        <li>Price IDs must match exactly with your Stripe Dashboard configuration</li>
                        <li>Display prices are shown to users in the upgrade modal and other UI elements</li>
                        <li>Changes to prices here do not automatically update Stripe - update both places</li>
                        <li>Environment variables (if set) are used as fallback when system settings are empty</li>
                    </ul>
                }
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
            />
        </div>
    );
};

export default BillingSettingsTab;
